/**
 * S3 key conventions for the event gallery.
 *
 * Layout:
 *   originals   uploads/<event>/<ts>_<name>
 *   thumbnails  thumb_uploads/<event>/<ts>_<name>
 *   sidecars    uploads/<event>/<ts>_<name>.json   (EXIF metadata)
 *
 * Legacy flat keys (no <event> segment) are still valid and are surfaced
 * under the "Uncategorized" pseudo-event.
 *
 * The `<ts>_<name>` filename format (epoch-ms prefix) is preserved on upload
 * for backward compatibility, but date sorting/grouping prefers the API's
 * `lastModified` and EXIF `dateTaken` over parsing it.
 */
import { S3_CONFIG, UNCATEGORIZED_EVENT } from '../config/awsConfig.ts';

const { UPLOAD_PREFIX, THUMBNAIL_PREFIX } = S3_CONFIG;
const SIDECAR_SUFFIX = '.json';

/** Characters not allowed in an event folder name (kept S3- and URL-safe). */
const UNSAFE_EVENT_CHARS = /[^a-zA-Z0-9 _-]+/g;

/**
 * Normalize a user-entered event name into a safe S3 path segment.
 * Collapses whitespace, strips slashes and other unsafe characters.
 * Returns '' for names that normalize to nothing.
 */
export function sanitizeEventName(raw: string): string {
  return raw
    .trim()
    .replace(UNSAFE_EVENT_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Strip a known leading prefix (`uploads/` or `thumb_uploads/`) from a key. */
function stripPrefix(key: string): { prefix: string; rest: string } {
  if (key.startsWith(`${THUMBNAIL_PREFIX}/`)) {
    return { prefix: THUMBNAIL_PREFIX, rest: key.slice(THUMBNAIL_PREFIX.length + 1) };
  }
  if (key.startsWith(`${UPLOAD_PREFIX}/`)) {
    return { prefix: UPLOAD_PREFIX, rest: key.slice(UPLOAD_PREFIX.length + 1) };
  }
  return { prefix: '', rest: key };
}

/** The filename component of a key, e.g. `1700000000000_photo.jpg`. */
export function basename(key: string): string {
  const parts = key.split('/');
  return parts[parts.length - 1] ?? '';
}

/**
 * The event folder for a key. Everything between the prefix and the basename.
 * Flat (prefix-only) keys map to the Uncategorized pseudo-event.
 */
export function eventFromKey(key: string): string {
  const { rest } = stripPrefix(key);
  const segments = rest.split('/');
  if (segments.length <= 1) return UNCATEGORIZED_EVENT;
  return segments.slice(0, -1).join('/');
}

/** True if this key is an EXIF sidecar (not a media file). */
export function isSidecarKey(key: string): boolean {
  return key.endsWith(SIDECAR_SUFFIX);
}

/** Build the S3 prefix used to list a single event's originals or thumbnails. */
export function eventPrefix(event: string, kind: 'original' | 'thumbnail' = 'thumbnail'): string {
  const base = kind === 'thumbnail' ? THUMBNAIL_PREFIX : UPLOAD_PREFIX;
  if (event === UNCATEGORIZED_EVENT) return `${base}/`;
  return `${base}/${event}/`;
}

/** Convert a thumbnail key to its original key (handles nested events). */
export function toOriginalKey(thumbKey: string): string {
  if (thumbKey.startsWith(`${THUMBNAIL_PREFIX}/`)) {
    return `${UPLOAD_PREFIX}/${thumbKey.slice(THUMBNAIL_PREFIX.length + 1)}`;
  }
  return thumbKey;
}

/** Convert an original key to its thumbnail key (handles nested events). */
export function toThumbnailKey(originalKey: string): string {
  if (originalKey.startsWith(`${UPLOAD_PREFIX}/`)) {
    return `${THUMBNAIL_PREFIX}/${originalKey.slice(UPLOAD_PREFIX.length + 1)}`;
  }
  return originalKey;
}

/** Sidecar JSON key for a given original key. */
export function toSidecarKey(originalKey: string): string {
  return `${originalKey}${SIDECAR_SUFFIX}`;
}

/** Strip the trailing `.json` of a sidecar key to get its original media key. */
export function fromSidecarKey(sidecarKey: string): string {
  return sidecarKey.endsWith(SIDECAR_SUFFIX)
    ? sidecarKey.slice(0, -SIDECAR_SUFFIX.length)
    : sidecarKey;
}

export interface UploadKeys {
  originalKey: string;
  thumbnailKey: string;
  sidecarKey: string;
  /** The human filename (without the timestamp prefix). */
  displayName: string;
}

/**
 * Build the trio of keys for a new upload.
 * @param timestamp epoch-ms; embedded as the filename prefix for legacy sorting.
 */
export function buildUploadKeys(event: string, fileName: string, timestamp: number): UploadKeys {
  const safeEvent = sanitizeEventName(event);
  const folder = safeEvent && safeEvent !== UNCATEGORIZED_EVENT ? `${safeEvent}/` : '';
  const stampedName = `${timestamp}_${fileName}`;
  const originalKey = `${UPLOAD_PREFIX}/${folder}${stampedName}`;
  return {
    originalKey,
    thumbnailKey: `${THUMBNAIL_PREFIX}/${folder}${stampedName}`,
    sidecarKey: toSidecarKey(originalKey),
    displayName: fileName,
  };
}

/** Human-facing filename: strip the leading `<ts>_` from the basename. */
export function displayName(key: string): string {
  const name = basename(key);
  const match = name.match(/^(\d{10,})_(.+)$/);
  return match ? match[2] : name;
}

/**
 * Best-effort capture timestamp parsed from the `<ts>_` filename prefix.
 * Returns null when the filename does not follow the convention.
 */
export function timestampFromKey(key: string): number | null {
  const match = basename(key).match(/^(\d{10,})_/);
  if (!match) return null;
  const ms = Number(match[1]);
  return Number.isFinite(ms) ? ms : null;
}
